import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { mapApiCarrierToCarrierRow, mapApiCarriersToCarrierRows } from "design-components";
import { carriersListInitialState } from "./carriers.models";
import { fetchCarriersList, inviteCarrier } from "./carriers.thunk";

const carriersListSlice = createSlice({
  name: "carriersList",
  initialState: carriersListInitialState,
  reducers: {
    setCarriersListSearch: (state, action: PayloadAction<string>) => {
      state.search = action.payload.trim();
    },
    setCarriersListSort: (
      state,
      action: PayloadAction<{ sortingOrder: "asc" | "desc"; sortingField?: string }>,
    ) => {
      state.sortingOrder = action.payload.sortingOrder;
      if (action.payload.sortingField) {
        state.sortingField = action.payload.sortingField;
      }
    },
    setCarriersListRange: (state, action: PayloadAction<{ fromIndex: number; toIndex: number }>) => {
      state.fromIndex = action.payload.fromIndex;
      state.toIndex = action.payload.toIndex;
    },
    setCarriersListOperatorIds: (state, action: PayloadAction<string[]>) => {
      state.selectedOperatorIds = [...action.payload];
    },
    setCarriersListCustomerIds: (state, action: PayloadAction<string[]>) => {
      state.selectedCustomerIds = [...action.payload];
    },
    setCarriersListShowOnlyActive: (state, action: PayloadAction<boolean>) => {
      state.showOnlyActive = action.payload;
    },
    setCarriersListDrawerFilters: (
      state,
      action: PayloadAction<{
        listFilterDotStatus: string[];
        listFilterRiskLevel: string[];
        listFilterAviroStatus: string[];
        listFilterLocationIds: string[];
      }>,
    ) => {
      state.listFilterDotStatus = [...action.payload.listFilterDotStatus];
      state.listFilterRiskLevel = [...action.payload.listFilterRiskLevel];
      state.listFilterAviroStatus = [...action.payload.listFilterAviroStatus];
      state.listFilterLocationIds = [...action.payload.listFilterLocationIds];
    },
    setCarriersListToolbarLocationFilters: (state, action: PayloadAction<string[]>) => {
      state.toolbarLocationFilterIds = [...action.payload];
    },
    removeCarriersListDrawerValue: (
      state,
      action: PayloadAction<{ sectionId: string; value: string }>,
    ) => {
      const { sectionId, value } = action.payload;
      if (sectionId === "dotStatus") {
        state.listFilterDotStatus = state.listFilterDotStatus.filter((v) => v !== value);
      } else if (sectionId === "riskLevel") {
        state.listFilterRiskLevel = state.listFilterRiskLevel.filter((v) => v !== value);
      } else if (sectionId === "aviroStatus") {
        state.listFilterAviroStatus = state.listFilterAviroStatus.filter((v) => v !== value);
      } else if (sectionId === "locations") {
        state.listFilterLocationIds = state.listFilterLocationIds.filter((v) => v !== value);
      }
    },
    clearCarriersListDrawerFilters: (state) => {
      state.listFilterDotStatus = [];
      state.listFilterRiskLevel = [];
      state.listFilterAviroStatus = [];
      state.listFilterLocationIds = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCarriersList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCarriersList.fulfilled, (state, action) => {
        state.loading = false;
        state.totalCarriers = action.payload.data.totalCount;
        state.rows = mapApiCarriersToCarrierRows(action.payload.data.carriers);
      })
      .addCase(fetchCarriersList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Failed to fetch carriers";
      })
      .addCase(inviteCarrier.fulfilled, (state, action) => {
        const item = action.payload.listItem;
        const exists = state.rows.some((row) => row.carrierId === item.carrierId);
        if (!exists) {
          state.rows = [mapApiCarrierToCarrierRow(item), ...state.rows];
          state.totalCarriers += 1;
        }
      });
  },
});

export const {
  setCarriersListSearch,
  setCarriersListSort,
  setCarriersListRange,
  setCarriersListOperatorIds,
  setCarriersListCustomerIds,
  setCarriersListShowOnlyActive,
  setCarriersListDrawerFilters,
  setCarriersListToolbarLocationFilters,
  removeCarriersListDrawerValue,
  clearCarriersListDrawerFilters,
} = carriersListSlice.actions;

export default carriersListSlice.reducer;
